import {
  KdsCallout,
  KdsCardFile,
  KdsDivider,
  KdsLink,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiIndeterminateCircleLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import {
  OrderItemDetailsReadDtoPreMediaStatusEnum,
  OrderItemReadDtoProofStatusEnum,
  SandboxItemReadDtoDispatchTypeEnum,
} from "@/config/apiClient";
import { useSandboxItems, useToasts } from "@/hooks";
import { useFile } from "@/hooks/queries/useFile";
import { RemoveHDFileOverlay } from "./_partials/RemoveHDFileOverlay";
import { type GraphismVersionBlocProps } from "./GraphismVersionBloc.types";

export const GraphismVersionBloc = ({
  hdFile,
  hasAModificationRequest,
  index,
  orderItem,
  version,
  onTabNavigation,
}: GraphismVersionBlocProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const [switchToConfirmDelete, setSwitchToConfirmDelete] = useState<
    string | undefined
  >(undefined);

  const { useDownloadFileHandler } = useFile();
  const { handleDownloadFile } = useDownloadFileHandler();

  const { useDeleteSandboxItemVersionFile } = useSandboxItems();
  const {
    mutateAsync: deleteSandboxItemVersionFile,
    isPending: isPendingDeleteSandboxItemVersionFile,
  } = useDeleteSandboxItemVersionFile();

  const sandboxItem = orderItem?.sandboxItem;

  const showDeleteGraphicButton =
    orderItem?.proofStatus !== OrderItemReadDtoProofStatusEnum.ProofValidated &&
    !sandboxItem?.withPreMedia &&
    !hasAModificationRequest;

  const hasAcceptedPreMediaPropositionFile = Boolean(
    sandboxItem?.withPreMedia &&
      orderItem?.preMediaStatus ===
        OrderItemDetailsReadDtoPreMediaStatusEnum.PropositionAccepted &&
      sandboxItem?.preMediaPropositionsFiles?.length
  );

  const handleDeleteSandboxItemVersionFile = async (params: {
    orderId: string;
    orderItemId: string;
    sandboxItemVersionId: string;
  }) => {
    const result = await deleteSandboxItemVersionFile({
      orderId: params.orderId,
      orderItemId: params.orderItemId,
      sandboxItemVersionId: params.sandboxItemVersionId,
    });

    if (result.status === 200) {
      add({
        state: "success",
        title: t("cardOrder.toast.successDeleteGraphism"),
      });
      setSwitchToConfirmDelete(undefined);
    } else {
      add({
        state: "error",
        title: t("cardOrder.toast.errorDeleteGraphism"),
      });
    }
  };

  return (
    <VStack key={`${version.id}-${version.name}`} gap="sp-md">
      {index !== 0 && <KdsDivider />}

      <VStack gap="sp-xs" wrap="wrap">
        <KdsText size="subtitle-sm" dataCy={`versionName-${index}`}>
          {version.name}
        </KdsText>

        <KdsText>
          {t("order.myOrdersItemDetails.versionName", {
            name: version.name,
          })}
        </KdsText>
      </VStack>

      <VStack gap="sp-sm">
        {sandboxItem.dispatchType !==
        SandboxItemReadDtoDispatchTypeEnum.DispatchFiles ? (
          <KdsText dataCy={`versionQuantity${index}`}>
            {t("order.myOrdersItemDetails.versionQuantity", {
              quantity:
                version.SandboxItemVersionDispatch?.reduce(
                  (sum, dispatch) => sum + dispatch.quantity,
                  0
                ) ?? 0,
            })}
          </KdsText>
        ) : (
          <>
            <KdsText dataCy={`versionQuantity${index}`}>
              {t("orderItemSummary.details.quantity")}
            </KdsText>

            <KdsLink size="sm" onPress={() => onTabNavigation("documents")}>
              {t("orderItemSummary.details.dispatchLink")}
            </KdsLink>
          </>
        )}

        {((!!hdFile && !sandboxItem.withPreMedia) ||
          (!!hdFile && hasAcceptedPreMediaPropositionFile)) && (
          <div className="relative overflow-hidden">
            <KdsCardFile
              isDeletable={showDeleteGraphicButton}
              onDelete={() => setSwitchToConfirmDelete(version.id)}
              isViewable
              isDownloadable
              fileName={hdFile?.fileName}
              fileSize={hdFile?.size}
              fileUrl={hdFile?.url}
              onDownload={() =>
                handleDownloadFile(hdFile?.fileName, hdFile?.url)
              }
            />

            {switchToConfirmDelete === version.id && (
              <RemoveHDFileOverlay
                onCancel={() => setSwitchToConfirmDelete(undefined)}
                onDelete={() =>
                  void handleDeleteSandboxItemVersionFile({
                    orderId: orderItem?.orderId,
                    orderItemId: orderItem?.id,
                    sandboxItemVersionId: switchToConfirmDelete,
                  })
                }
                isLoading={isPendingDeleteSandboxItemVersionFile}
              />
            )}
          </div>
        )}

        <KdsCallout
          visible={!hdFile && !sandboxItem.withPreMedia}
          title={t(
            "order.myOrdersItemDetails.callout.missingDesignVersion.title"
          )}
          state="error"
          iconLeft={RiIndeterminateCircleLine}
        >
          <KdsText>
            {t(
              "order.myOrdersItemDetails.callout.missingDesignVersion.content"
            )}
          </KdsText>
        </KdsCallout>
      </VStack>
    </VStack>
  );
};
